import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAllSearchableContent } from '../data/searchData';
import { calculateMatchScore, fuzzyMatch, isValidQuery, getSearchSuggestions } from '../utils/searchUtils';

// Custom hook for search functionality
export const useSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  
  // Get search data
  const searchData = useMemo(() => getAllSearchableContent(), []);
  
  // Load recent searches from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('rayad-recent-searches');
      if (stored) {
        const parsed = JSON.parse(stored);
        setRecentSearches(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('Error loading recent searches:', error);
    }
  }, []);
  
  // Save recent searches to localStorage
  const saveRecentSearches = useCallback((searches) => {
    try {
      localStorage.setItem('rayad-recent-searches', JSON.stringify(searches));
    } catch (error) {
      console.error('Error saving recent searches:', error);
    }
  }, []);
  
  // Add to recent searches
  const addToRecentSearches = useCallback((searchQuery) => {
    if (!isValidQuery(searchQuery)) return;
    
    const normalizedQuery = searchQuery.trim().toLowerCase();
    setRecentSearches(prev => {
      // Remove if already exists
      const filtered = prev.filter(item => item.toLowerCase() !== normalizedQuery);
      // Add to beginning
      const updated = [searchQuery.trim(), ...filtered].slice(0, 5);
      saveRecentSearches(updated);
      return updated;
    });
  }, [saveRecentSearches]);
  
  // Clear recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem('rayad-recent-searches');
    } catch (error) {
      console.error('Error clearing recent searches:', error);
    }
  }, []);
  
  // Perform search with debouncing
  const performSearch = useCallback(async (searchQuery) => {
    if (!isValidQuery(searchQuery)) {
      setResults([]);
      setSuggestions([]);
      return;
    }
    
    setIsLoading(true);
    
    // Simulate async operation for better UX
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Filter and score items
      const scoredResults = searchData
        .map(item => ({
          ...item,
          score: calculateMatchScore(searchQuery, item)
        }))
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score);
      
      setResults(scoredResults);
      
      // Generate suggestions
      const searchSuggestions = getSearchSuggestions(searchQuery, searchData);
      setSuggestions(searchSuggestions);
      
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchData]);
  
  // Update query and perform search
  const updateQuery = useCallback((newQuery) => {
    setQuery(newQuery);
    performSearch(newQuery);
  }, [performSearch]);
  
  // Execute search and add to recent searches
  const executeSearch = useCallback((searchQuery = query) => {
    if (isValidQuery(searchQuery)) {
      addToRecentSearches(searchQuery);
      performSearch(searchQuery);
    }
  }, [query, addToRecentSearches, performSearch]);
  
  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setSuggestions([]);
  }, []);
  
  // Get results by category
  const getResultsByCategory = useCallback(() => {
    const categorized = {
      Main: [],
      Portfolio: [],
      Media: []
    };
    
    results.forEach(result => {
      if (categorized[result.category]) {
        categorized[result.category].push(result);
      }
    });
    
    return categorized;
  }, [results]);
  
  // Check if any results exist
  const hasResults = results.length > 0;
  const hasQuery = isValidQuery(query);
  
  // Get popular/default suggestions
  const getPopularSuggestions = useCallback(() => {
    return [
      'Software Development',
      'Cyber Security', 
      'Immigration',
      'Blockchain',
      'Art Gallery',
      'About',
      'Contact'
    ];
  }, []);
  
  return {
    // State
    query,
    results,
    isLoading,
    recentSearches,
    suggestions,
    hasResults,
    hasQuery,
    
    // Actions
    updateQuery,
    executeSearch,
    clearSearch,
    clearRecentSearches,
    getResultsByCategory,
    getPopularSuggestions,
    
    // Utilities
    performSearch,
    addToRecentSearches
  };
};

// Hook for search modal state management
export const useSearchModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);
  
  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);
  
  const toggleModal = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  
  // Keyboard shortcut effect
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl/Cmd + K to open search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        openModal();
      }
      
      // Escape to close
      if (event.key === 'Escape' && isOpen) {
        closeModal();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, openModal, closeModal]);
  
  return {
    isOpen,
    openModal,
    closeModal,
    toggleModal
  };
};